/**
 * What LITH-03 does with a surface that is not flat.
 *
 * A scanner focuses on one plane. If the wafer surface varies across the
 * exposure field, part of that field is printed out of focus, and the scanner
 * has no way to avoid it - there is one focal plane and the surface has a
 * range. So whatever CMP-11 failed to flatten is spent, directly and without
 * any conversion factor, out of the next layer's focus budget.
 *
 * THIS EDGE NEEDED NO NEW PHYSICS
 *
 * Which is why it was worth building first among the remaining couplings.
 * `expose` already computes critical dimension against focus - that is the
 * process window the module was built around. Feeding it a focus offset taken
 * from the incoming topography is not a model, it is an argument.
 *
 * The consequence is the one a process integrator lives with: a CMP problem
 * shows up two layers later as a lithography problem, on a scanner that is
 * working perfectly, and the CD chart at the scanner is where somebody first
 * notices.
 */

import { DEFAULT_LITHO_RECIPE, expose, type LithoRecipe } from './LithoSim'
import { DUV_IMMERSION, type Illumination } from './optics'

export interface TopographyPrint {
  /** Surface range the wafer arrived with [nm]. */
  surfaceRangeNm: number
  /** CD printed at the focal plane [nm]. */
  cdBestNm: number
  /** CD printed at the worst point in the field [nm]. */
  cdWorstNm: number
  /** Spread of CD across the field caused by the topography alone [nm]. */
  cdRangeNm: number
  /** Image contrast at the worst point in the field. */
  worstContrast: number
  /** True when some part of the field did not print at all. */
  failedSomewhere: boolean
  /** Fraction of the CD budget the incoming topography consumed. */
  budgetUsed: number
}

/** How much CD variation the process is allowed in total [nm]. */
export const CD_BUDGET_NM = 4.5

/**
 * Print a layer on a wafer with a given surface range.
 *
 * The field spans the whole range, so the scanner's focal plane can sit at best
 * in the middle of it: half the range above, half below. Both extremes are
 * defocused by the same amount, which is why the worst case is evaluated at
 * plus and minus half and not at the full range.
 */
export function printOnTopography(
  surfaceRangeNm: number,
  recipe: LithoRecipe = DEFAULT_LITHO_RECIPE,
  ill: Illumination = DUV_IMMERSION,
): TopographyPrint {
  const half = surfaceRangeNm / 2
  const shots = [0, half, -half].map((d) =>
    expose({ ...recipe, focusNm: recipe.focusNm + d }, ill),
  )

  const cds = shots.map((s) => s.cdNm)
  const cdBestNm = shots[0].cdNm
  // Whichever end of the field is furthest from where it should be.
  const cdWorstNm = cds.reduce((a, b) => (Math.abs(b - cdBestNm) > Math.abs(a - cdBestNm) ? b : a))
  const cdRangeNm = Math.max(...cds) - Math.min(...cds)

  return {
    surfaceRangeNm,
    cdBestNm,
    cdWorstNm,
    cdRangeNm,
    worstContrast: Math.min(...shots.map((s) => s.contrast)),
    failedSomewhere: shots.some((s) => s.failedToPrint),
    budgetUsed: cdRangeNm / CD_BUDGET_NM,
  }
}
